import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, Mail, MapPin, Clock, Users, Gift } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useLanguage } from "@/contexts/LanguageContext";

const Contact = () => {
  const { t } = useLanguage();
  const { ref: sectionRef, isVisible } = useScrollAnimation(0.2);
  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: t('contact.phone'),
      content: "040 47 00 60",
      link: "tel:040470060"
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: t('contact.email'),
      content: "curryskanor@gmail.com",
      link: "mailto:curryskanor@gmail.com"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: t('contact.address'),
      content: "Falsterbovägen 21, 239 33 Skanör",
      link: "https://www.google.com/maps/search/?api=1&query=Falsterbovägen+21,+239+33+Skanör"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: t('contact.hours'),
      content: t('contact.seeBelow'),
      link: "#"
    }
  ];

  const services = [
    {
      icon: <Users className="w-8 h-8" />,
      title: t('contact.catering'),
      description: t('contact.cateringDesc')
    },
    {
      icon: <Gift className="w-8 h-8" />,
      title: t('contact.celebrate'),
      description: t('contact.celebrateDesc')
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: t('contact.accessible'),
      description: t('contact.accessibleDesc')
    }
  ];

  return (
    <section id="contact" className="section-padding bg-background" ref={sectionRef}>
      <div className="container-custom">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-gold text-lg font-medium tracking-wider uppercase mb-4 animate-text-glow">{t('contact.title')}</p>
          <h2 className="heading-luxury text-foreground mb-6 text-gradient">
            {t('contact.heading')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('contact.description')}
          </p>
        </div>

        {/* Contact Info Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16">
          {contactInfo.map((info, index) => (
            <Card
              key={index}
              className={`group border-border bg-card transition-all duration-500 hover:shadow-[0_15px_40px_rgba(212,175,55,0.2)] hover:-translate-y-2 ${
                isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
              }`}
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <CardContent className="p-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gold/10 text-gold mb-4 transition-all duration-300 group-hover:scale-110 group-hover:bg-gold/20">
                  {info.icon}
                </div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
                  {info.title}
                </h3>
                {info.link !== "#" ? (
                  <a
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-lg font-medium text-foreground hover:text-gold transition-colors"
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="text-lg font-medium text-foreground">{info.content}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Opening Hours Details */}
        <div className={`mb-16 max-w-4xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: '0.4s' }}>
          <h3 className="text-3xl font-serif font-semibold text-center text-foreground mb-8 text-gradient">
            {t('contact.hoursTitle')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-border bg-card hover-lift">
              <CardContent className="p-8">
                <h4 className="text-2xl font-serif font-semibold text-gold mb-6 text-center">
                  {t('contact.winter')}
                </h4>
                <div className="space-y-3 text-center">
                  <p className="text-lg text-foreground">
                    <span className="font-medium">{t('contact.monThu')}</span> 16:00 – 21:00
                  </p>
                  <p className="text-lg text-foreground">
                    <span className="font-medium">{t('contact.friSun')}</span> 16:00 – 22:00
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-card hover-lift">
              <CardContent className="p-8">
                <h4 className="text-2xl font-serif font-semibold text-gold mb-6 text-center">
                  {t('contact.summer')}
                </h4>
                <div className="space-y-3 text-center">
                  <p className="text-lg text-foreground">
                    <span className="font-medium">{t('contact.monThu')}</span> 16:00 – 22:00
                  </p>
                  <p className="text-lg text-foreground">
                    <span className="font-medium">{t('contact.friSun')}</span> 16:00 – 22:30
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
          <p className="text-sm text-muted-foreground italic text-center mt-6">
            {t('contact.kitchenNote')}
          </p>
        </div>

        {/* Services */}
        <div className="mb-16">
          <h3 className={`text-3xl font-serif font-semibold text-center text-foreground text-gradient mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.6s' }}>
            {t('contact.servicesTitle')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className={`group text-center p-8 bg-card rounded-xl border border-border transition-all duration-500 hover:shadow-[0_15px_40px_rgba(212,175,55,0.2)] hover:-translate-y-2 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${0.7 + index * 0.1}s` }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/10 text-gold mb-4 transition-all duration-300 group-hover:scale-110 group-hover:bg-gold/20">
                  {service.icon}
                </div>
                <h4 className="text-xl font-serif font-semibold text-foreground mb-2 transition-colors group-hover:text-gold">
                  {service.title}
                </h4>
                <p className="text-muted-foreground">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className={`text-center p-12 bg-gradient-to-r from-gold/10 to-burgundy/10 rounded-2xl border border-gold/20 transition-all duration-700 ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        }`} style={{ transitionDelay: '1s' }}>
          <h3 className="text-3xl font-serif font-semibold text-foreground mb-4">
            {t('contact.ctaTitle')}
          </h3>
          <p className="text-lg text-muted-foreground mb-8">
            {t('contact.ctaDesc')}
          </p>
          <Button
            size="lg"
            onClick={() => {
              document.getElementById('booking')?.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
              });
            }}
            className="bg-gold hover:bg-gold-light text-background font-semibold text-lg px-10 py-6 shadow-gold transform hover:scale-105 transition-all"
          >
            {t('contact.bookNow')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Contact;
